import React, { useEffect, useState } from "react";
import {
  StyleSheet,
  View,
  Text,
  Dimensions,
  Linking,
  Platform,
} from "react-native";
import {
  ImageModal,
  MainText,
  PressableOpacity,
  VideoModal,
} from "app/components/common";
import * as Progress from "react-native-progress";
import Colors from "@microdoing/shared/constants/Colors";
import Theme from "@microdoing/shared/constants/Theme";
import normalizeSource from "app/utils/normalizeSource";
import hasImageExtension from "@microdoing/shared/utils/hasImageExtension";
import AudioMessage from "app/scenes/MessageScreen/ChatScreen/AudioMessage";
import CloseRoundIcon from "app/components/Icons/CloseRoundIcon";
import theme from "app/Theme/Theme";
import DocumentIcon from "app/components/Icons/DocumentIcon";
import normalize from "app/utils/normalize";

const { height, width } = Dimensions.get("window");

const PREVIEW_SIZE = 70;
const CLOSE_WIDTH = 10;

const styles = StyleSheet.create({
  main: {
    marginRight: theme.spacing.sm,
    borderRadius: 4,
    justifyContent: "center",
  },
  cancelButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    height: CLOSE_WIDTH,
    width: CLOSE_WIDTH,
    borderRadius: CLOSE_WIDTH / 2,
    position: "absolute",
    backgroundColor: theme.colors["neutral.white"],
    top: -10,
    right: -10,
    zIndex: 1,
  },
  icon: {
    position: "relative",
    top: 0,
    right: 0,
    zIndex: 10,
    color: theme.colors["neutral.white"],
  },
  image: {
    width: PREVIEW_SIZE,
    height: PREVIEW_SIZE,
    borderRadius: 4,
  },
  percent: {
    marginTop: 5,
    alignSelf: "center",
    textAlign: "center",
    fontSize: Theme.FONT_SIZE_SMALL,
  },
  progressBar: {
    marginTop: 6,
  },
  iconBox: {
    height: PREVIEW_SIZE - 25,
    width: PREVIEW_SIZE - 25,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    backgroundColor: theme.colors["brand.500"],
  },
  iconText: {
    textAlign: "center",
    marginTop: 5,
    fontSize: 12,
  },
  documentView: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
  },
});

const UploadPreview = ({
  preview = "",
  uri,
  fileUrl = "",
  fileName = "",
  isVideo = false,
  isAudio = false,
  cancelSelection = () => {},
  uploadProgress = 0,
  hasCancelButton = true,
  previewSize = PREVIEW_SIZE,
  closeWidth = CLOSE_WIDTH,
  style = {},
  imageStyle = {},
  imageResizeMode = "cover",
  isComingFromReflexiveAnalysis = false,
}) => {
  const [widthStyle, setWidthStyle] = useState(PREVIEW_SIZE);
  const [progressWidth, setProgressWidth] = useState(PREVIEW_SIZE);

  useEffect(() => {
    if (!isAudio) {
      setWidthStyle(previewSize);
      setProgressWidth(previewSize);
    } else if (Platform.OS === "web") {
      setWidthStyle("fit-content");
      setProgressWidth(previewSize);
    } else if (!isComingFromReflexiveAnalysis) {
      setWidthStyle("auto");
      setProgressWidth(normalize(70));
    } else {
      setWidthStyle(normalize(150));
      setProgressWidth(normalize(150));
    }
  }, []);

  const renderPreview = () => {
    if (isAudio) {
      if (Platform.OS !== "web") {
        return <AudioMessage soundUrl={uri} hasDarkBackground={false} />;
      } else {
        return <audio id="audio" controls src={uri}></audio>;
      }
    }

    if (isVideo) {
      return (
        <VideoModal
          maxHeight={previewSize}
          commentaryWidth={previewSize}
          imagePreviewStyle={[
            { width: previewSize, height: previewSize },
            imageStyle,
          ]}
          imagePreview={normalizeSource(preview)}
          source={{ uri: fileUrl || uri }}
          video={fileUrl || uri}
        />
      );
    }

    if (
      hasImageExtension(uri) ||
      hasImageExtension(preview) ||
      hasImageExtension(fileName)
    ) {
      return (
        <ImageModal
          maxHeight={previewSize}
          modalImageStyle={[
            styles.image,
            { width: previewSize, height: previewSize },
            imageStyle,
          ]}
          resizeMode={imageResizeMode}
          modalImageResizeMode="contain"
          style={[
            styles.image,
            { width: previewSize, height: previewSize },
            imageStyle,
          ]}
          source={{
            uri: normalizeSource(preview || uri),
          }}
        />
      );
    }

    return (
      <PressableOpacity
        style={styles.documentView}
        disabled={!fileUrl}
        onPress={() => Linking.openURL(fileUrl || "")}
      >
        <View style={[styles.image, styles.iconBox]}>
          <DocumentIcon color={theme.colors["neutral.white"]} />
        </View>
        <Text style={styles.iconText} numberOfLines={2}>
          {fileName}
        </Text>
      </PressableOpacity>
    );
  };

  return (
    <View
      style={[
        styles.main,
        {
          position: "relative",
          overflow: "visible",
          height: "auto",
          marginLeft: isAudio ? normalize(8) : 0,
        },
        style,
      ]}
    >
      {hasCancelButton && (
        <PressableOpacity
          onPress={cancelSelection}
          style={[
            styles.cancelButton,
            {
              height: closeWidth,
              width: closeWidth,
              top: isAudio ? -10 : -5,
              right: isAudio ? -10 : -5,
            },
          ]}
        >
          <CloseRoundIcon
            color={theme.colors["brand.500"]}
            width={normalize(18)}
            height={normalize(18)}
          />
        </PressableOpacity>
      )}
      {renderPreview()}
      {uploadProgress > 0 && (
        <Progress.Bar
          progress={uploadProgress}
          width={progressWidth}
          height={2}
          borderRadius={8}
          useNativeDriver
          borderWidth={0}
          color={Colors.mediumGreen}
          unfilledColor={"#ECEBF1"}
          style={styles.progressBar}
        />
      )}
      {uploadProgress > 0 && (
        <MainText
          marginTop="xs"
          alignSelf="center"
          textAlign="center"
          variant="l2-regular"
        >
          {(uploadProgress * 100).toFixed(0)}%
        </MainText>
      )}
    </View>
  );
};

export default UploadPreview;
